import { Button } from "@app/components/ui/button"; // Shadcn Button
import {
  Collapsible,
  CollapsibleContent,
} from "@app/components/ui/collapsible";
import { cn } from "@app/lib/utils"; // Shadcn utility
import {
  ArrowBigLeft,
  ArrowBigRight,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCcw,
} from "lucide-react"; // Icons
import { useState, type MouseEvent } from "react";
import { graphql, useMutation, useRefetchableFragment } from "react-relay";
import type { stakeholderRow_stakeholder$key } from "@app/queries/stakeholderRow_stakeholder.graphql";
import { ShortNumber } from "../short-number";
import StreamHistogram from "../stream-histogram";
import TransactionHistogram from "../txn-histogram";

const resetStreamsMutation = graphql`
  mutation stakeholderRow_StakeholderRowResetStreamsMutation(
    $input: ResetStreamsInput!
  ) {
    resetStreams(input: $input) {
      # Assuming the payload returns a 'stakeholder' field
      stakeholder {
        id
        # We request the NEW streams here.
        # Relay matches the 'id' above and updates the store automatically.
        streams {
          id
          morph {
            id
            kind
          }
          flows {
            amount
            kind
            date
          }
          createdAt
        }
      }
    }
  }
`;

// The raw txns + normalized streams render ONLY when a row is expanded, and a
// single counterparty can hold tens of thousands of txns — so they are gated
// behind `loadDetails` and fetched (through this @refetchable fragment's query)
// only when the row opens. The list load selects zero of them; expanding one
// row fetches only that stakeholder's own detail. monthlyCosts stays in the
// always-visible row, so it is not gated.
const fragment = graphql`
  fragment stakeholderRow_stakeholder on Stakeholder
  @refetchable(queryName: "StakeholderRowRefetchQuery")
  @argumentDefinitions(
    startDate: { type: "String!" }
    endDate: { type: "String!" }
    loadDetails: { type: "Boolean", defaultValue: false }
  ) {
    name
    txns @include(if: $loadDetails) {
      id
      kind
      postingDate
      amount
      description
      bankAccount {
        id
        last4
        nickname
        bank {
          name
          iconSvg
        }
      }
    }
    streams @include(if: $loadDetails) {
      id
      morph {
        id
        kind
      }
      flows {
        amount
        kind
        date
      }
      createdAt
    }
    monthlyCosts(startDate: $startDate, endDate: $endDate) {
      month
      amount
    }
  }
`;

export default function StakeholderRow({
  name,
  stakeholderKey,
  isEven,
}: {
  name: string;
  stakeholderKey: stakeholderRow_stakeholder$key;
  isEven: boolean;
}) {
  const [data, refetch] = useRefetchableFragment(fragment, stakeholderKey);
  const [isOpen, setIsOpen] = useState(false);
  // Detail (txns/streams) is fetched lazily the first time the row opens.
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Calculate dynamic colSpan: Name + MonthlyCosts + Actions
  const colSpanCount = 1 + (data.monthlyCosts?.length || 0) + 1;
  const [visibleStreamIndex, setVisibleStreamIndex] = useState(0);
  const [commitReset, isResetting] = useMutation(resetStreamsMutation);

  const rowBgClass = isEven ? "bg-slate-100" : "bg-white";

  // txns/streams are only present once loadDetails has been fetched (gated by
  // @include) — treat them as possibly-absent until then.
  const streams = data.streams ?? [];
  const currentStream = streams[visibleStreamIndex];

  const openRow = () => {
    const next = !isOpen;
    setIsOpen(next);
    // First expand: pull this one stakeholder's txns + streams.
    if (next && !detailsLoaded && !loadingDetails) {
      setLoadingDetails(true);
      refetch(
        { loadDetails: true },
        {
          fetchPolicy: "store-or-network",
          onComplete: () => {
            setDetailsLoaded(true);
            setLoadingDetails(false);
          },
        }
      );
    }
  };

  const handleResetStreams = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent row toggle

    commitReset({
      variables: {
        input: {
          stakeholderId: data.id, // Ensure your fragment requests 'id' if not already
          // Add other required mutation inputs here
        },
      },
      onCompleted: () => {
        // 3. Reset UI state
        // Important: New data might have fewer items than current index
        setVisibleStreamIndex(0);
      },
      onError: (err) => {
        console.error("Mutation failed", err);
      },
    });
  };

  const detailReady = detailsLoaded && !loadingDetails && !!data.txns;

  return (
    <>
      {/* --- Main Row (Unchanged) --- */}
      <tr
        className={cn(
          "cursor-pointer hover:bg-[#D2E1F0] border-x-2",
          rowBgClass,
          isOpen && "bg-slate-50 border-l-4 border-l-primary"
        )}
        onClick={openRow}
      >
        <td className="p-0 align-middle font-medium">
          <div className="flex items-center gap-2 p-2">
            <Button variant="ghost" size="icon" className="h-6 w-6">
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            {data.name}
          </div>
        </td>
        {data.monthlyCosts.map((cost) => (
          <td
            key={cost.month}
            className="p-2 align-middle text-right font-mono"
          >
            {cost.amount > 0 ? (
              <span className={cn({ "opacity-40": cost.amount < 100000 })}>
                <ShortNumber value={cost.amount / 100}></ShortNumber>
              </span>
            ) : (
              <span className="opacity-20">0</span>
            )}
          </td>
        ))}
        <td className="p-2 align-middle text-center">
          {/* ... existing button ... */}
        </td>
      </tr>

      {/* --- Details Row --- */}
      {isOpen && (
        <tr className="bg-slate-50/50 border-b-2">
          <td colSpan={colSpanCount} className="p-0">
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                {!detailReady ? (
                  <div className="p-10 flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading transactions…
                  </div>
                ) : (
                  <div className="p-6 flex md:flex-row justify-center border border-[red] gap-8">
                    {/* LEFT: Raw Transactions */}
                    <div className="flex flex-col items-center">
                      <h4 className="text-sm font-semibold text-center w-full h-10 flex justify-center"></h4>
                      <TransactionHistogram
                        stakeholderName={name}
                        data={{ txns: data.txns ?? [] }}
                        ratio={0.8}
                      />
                    </div>

                    {/* CENTER: Transformation Arrow */}
                    <div className="flex items-center justify-center text-muted-foreground/50">
                      <ArrowBigRight className="w-12 h-12" />
                    </div>

                    {/* RIGHT: Normalized Streams */}
                    <div className="flex flex-col items-center">
                      {/* Stream Navigation Header */}
                      <div className="flex items-center gap-4 w-full">
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={visibleStreamIndex === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisibleStreamIndex((prev) =>
                              Math.max(0, prev - 1)
                            );
                          }}
                        >
                          <ArrowBigLeft className="h-4 w-4" />
                        </Button>
                        <h4 className="text-sm font-semibold text-center flex-1 h-10 flex items-center justify-center">
                          {streams.length > 0 ? (
                            <>
                              Stream {visibleStreamIndex + 1} of {streams.length}:
                              {currentStream && (
                                <div className="text-primary truncate ml-2">
                                  {currentStream.morph.kind}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">
                              No Streams
                            </span>
                          )}
                        </h4>
                        <div className="mb-4">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleResetStreams}
                            disabled={isResetting}
                          >
                            {isResetting ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <RefreshCcw className="h-4 w-4 mr-2" />
                            )}
                            Reset Streams
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={visibleStreamIndex >= streams.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisibleStreamIndex((prev) =>
                              Math.min(streams.length - 1, prev + 1)
                            );
                          }}
                        >
                          <ArrowBigRight className="h-4 w-4" />
                        </Button>
                      </div>
                      {/* Stream Histogram Wrapper */}
                      <div className="relative w-full h-[400px]">
                        {streams.length > 0 && currentStream ? (
                          <StreamHistogram
                            key={currentStream.id}
                            stream={currentStream}
                            ratio={0.8}
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center border border-dashed rounded-xl text-muted-foreground">
                            No normalized streams found
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </td>
        </tr>
      )}
    </>
  );
}
